import { FormattedMessage } from 'react-intl';
import {
  Box,
  Text,
  H1,
  List,
  Container,
  Anchor,
  LoadableButton,
  Alert,
  Li,
  Loading,
  Link,
  CheckboxStateless
} from '../components';
import {
  currentUserQuery,
  settings as settingsQuery,
  updateConsentsMutation,
  updateConsentsMutationVariables
} from '../core-types';
import { globalMessages } from '../lib/messages';
import { useOnboardingQuery } from './Onboarding/useOnboardingQuery';
import { CURRENT_USER_QUERY, useCurrentUser } from '../lib/core/useCurrentUser';
import { useSystem } from '../lib/core/useSystem';
import { useUpdateCurrentUserMutation } from '../lib/core/mutations';
import { useToasts } from '../components/Toasts';
import { useMutation, gql, useQuery } from '@apollo/client';
import { ConsentCheckBox } from './TermsAndConditions';
import { SETTINGS_QUERY } from '../lib/core/SettingsQuery';
import { useState } from 'react';
import { ACCEPT_MULTIPLE_CONSENTS_MUTATION } from './VerifyPhoneNumber/graphql';
import { enforceLegalConsents } from '../modules/VerifyPhoneNumber/CreateVerification';
import messages from '../modules/VerifyPhoneNumber/messages';

export const ACCEPT_TERMS_OF_USE = gql`
  mutation acceptTermsOfUseMutation {
    acceptTermsOfUse
  }
`;

function AgreedToSendDataToThirdPartiesForm() {
  const { addToast } = useToasts();
  const { currentUser } = useCurrentUser();
  const [updateCurrentUser, { loading }] = useUpdateCurrentUserMutation({
    onError: () => {
      addToast({
        text: <FormattedMessage {...globalMessages.genericError} />,
        type: 'error'
      });
    }
  });
  if (!currentUser) {
    return null;
  }
  return (
    <Text textAlign="left">
      <CheckboxStateless
        name="agreedToSendDataToThirdParties"
        onChange={event => {
          updateCurrentUser({
            variables: {
              input: { agreedToSendDataToThirdParties: event.target.checked }
            }
          });
        }}
        checked={currentUser.agreedToSendDataToThirdParties || false}
        label={
          <FormattedMessage
            id="acceptTerms.showAgreedToSendDataToThirdPartiesLabel"
            defaultMessage="Share contact data with our partners for statistical and profiling purposes"
          />
        }
        disabled={loading}
      />
    </Text>
  );
}

export const TermsStep = ({ goNext }: { goNext: () => void }) => {
  const system = useSystem();
  const onboardingQueryResult = useOnboardingQuery();

  const [consents, setConsents] = useState({
    terms_of_use_and_privacy_policy: false,
    registration_agreement_withdrawal: false,
    subscription_agreement_withdrawal: false,
    send_data_to_third_parties: false
  });

  const { data: settingsData } = useQuery<settingsQuery, {}>(SETTINGS_QUERY);

  const handleCheckBoxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setConsents({
      ...consents,
      [event.target.name]: event.target.checked
    });
  };

  const {
    terms_of_use_and_privacy_policy,
    registration_agreement_withdrawal,
    subscription_agreement_withdrawal,
    send_data_to_third_parties
  } = consents;

  const [legalConsentsError, setLegalConsentsError] = useState(false);
  const [acceptTerms, { loading, error }] = useMutation<
    updateConsentsMutation,
    updateConsentsMutationVariables
  >(ACCEPT_MULTIPLE_CONSENTS_MUTATION, {
    variables: {
      consents:
        settingsData?.system?.legalConsents?.map(consent => {
          return {
            type: consent.type,
            enforcement: consent.enforcement,
            accepted: consents[consent.type]
          };
        }) || []
    },
    onCompleted: () => {
      goNext();
    },
    update: (cache, { data }) => {
      if (!data) {
        return;
      }
      const currentUserResult = cache.readQuery<currentUserQuery>({
        query: CURRENT_USER_QUERY
      });
      if (!currentUserResult || !currentUserResult.currentUser) {
        return;
      }
      cache.writeQuery<currentUserQuery>({
        query: CURRENT_USER_QUERY,
        data: {
          ...currentUserResult,
          currentUser: currentUserResult.currentUser
            ? {
                ...currentUserResult.currentUser,
                agreedToTerms: terms_of_use_and_privacy_policy,
                agreedToSendDataToThirdParties: send_data_to_third_parties,
                agreedToRegistrationAgreementWithdrawal: registration_agreement_withdrawal,
                agreedToSubscriptionAgreementWithdrawal: subscription_agreement_withdrawal
              }
            : null
        }
      });
    }
  });

  const termsOfConditionItems =
    onboardingQueryResult?.data?.buyProductPage?.termsOfConditionItems;

  return (
    <Container variant="narrow" textAlign="center">
      <Box>
        <H1 typoStyle="lg">
          <FormattedMessage
            id="terms_have_changed"
            defaultMessage="Terms have changed"
          />
        </H1>
        <Text mb="xs">
          <FormattedMessage
            id="terms_have_changed_description"
            defaultMessage="Our Terms of Use have changed, and we require you to accept these updated agreements in order to continue using the service."
          />
        </Text>
      </Box>

      {onboardingQueryResult.loading && <Loading />}
      {onboardingQueryResult.error && (
        <Alert variant="error">
          <FormattedMessage {...globalMessages.genericError} />
        </Alert>
      )}
      {termsOfConditionItems && (
        <List data-testid="TermsList">
          {termsOfConditionItems.map(item => {
            if (!item || !item.title || !item.body) {
              return null;
            }
            return (
              <Li mb="xs" key={item._key}>
                <Text bold mb={1}>
                  {item.title}
                </Text>
                <Text>{item.body}</Text>
              </Li>
            );
          })}
        </List>
      )}
      {system.configuration.showAgreedToSendDataToThirdPartiesCheckbox && (
        <AgreedToSendDataToThirdPartiesForm />
      )}
      <Box mb={5}>
        {settingsData?.system?.legalConsents?.map(consent => {
          return (
            <Box mb={4} key={consent.type} textAlign="left">
              <ConsentCheckBox
                consentType={consent.type}
                settingsData={settingsData}
                terms_of_use_and_privacy_policy={
                  terms_of_use_and_privacy_policy
                }
                registration_agreement_withdrawal={
                  registration_agreement_withdrawal
                }
                subscription_agreement_withdrawal={
                  subscription_agreement_withdrawal
                }
                send_data_to_third_parties={send_data_to_third_parties}
                handleCheckBoxChange={handleCheckBoxChange}
              />
            </Box>
          );
        })}
      </Box>
      {error && (
        <Alert variant="error">
          <FormattedMessage
            id="acceptTerms.error"
            defaultMessage="Could not update terms, please try again"
          />
        </Alert>
      )}
      {legalConsentsError && (
        <Alert variant="error">
          <FormattedMessage {...messages.termsNotAccepted} />
        </Alert>
      )}
      <LoadableButton
        variant="primary"
        shape="cool"
        onClick={() => {
          if (
            settingsData &&
            settingsData.system &&
            settingsData.system.legalConsents
          ) {
            const legalConsents = settingsData.system.legalConsents;
            const legalConsentsAccepted = enforceLegalConsents(
              legalConsents,
              consents
            );
            if (!legalConsentsAccepted) {
              setLegalConsentsError(true);
              return;
            }
          }
          acceptTerms();
        }}
        disabled={!termsOfConditionItems}
        loading={loading}
        responseState={error ? 'error' : 'success'}
        data-testid="Terms__Accept"
        mb="xs"
        mt="xs"
      >
        <FormattedMessage
          id="modules.BuyProduct.TermsStep.button"
          defaultMessage="Accept terms of use"
        />
      </LoadableButton>
      <Box mb="sm">
        <Link route="terms-of-use" passHref>
          <Anchor target="blank">
            <FormattedMessage
              id="modules.BuyProduct.TermsStep.link"
              defaultMessage="See complete terms of use"
            />
          </Anchor>
        </Link>
      </Box>
    </Container>
  );
};
